import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { PrimaryButton, SecondaryButton, Field, inputCls } from "@/components/ui-kit";
import { X, RefreshCw, Save, FileSignature, Download, Cloud, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { sanitizeHtml } from "@/lib/sanitize";

const CONTRACT_STYLES = `
  .aa-doc { font-family: Georgia, "Times New Roman", serif; color: #222; line-height: 1.55; font-size: 13px; padding: 20px; background: white; }
  .aa-doc h1 { font-family: 'Cabinet Grotesk', sans-serif; }
  .aa-doc h3 { font-family: 'Cabinet Grotesk', sans-serif; font-size: 14px; margin-top: 18px; margin-bottom: 6px; text-transform: uppercase; letter-spacing: .5px; }
  .aa-doc ul { padding-left: 20px; }
  .aa-doc li { margin-bottom: 4px; }
`;

export default function ContractModal({ customerId, customerName, onClose }) {
  const [contract, setContract] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState("UF");
  const [priceDate, setPriceDate] = useState(new Date().toISOString().slice(0, 10));
  const [ufRate, setUfRate] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/customers/${customerId}/contract`);
    setContract(data);
    if (data.currency) setCurrency(data.currency);
    if (data.price_date) setPriceDate(data.price_date);
    if (data.uf_rate_used) setUfRate(data.uf_rate_used);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  // Preview de la UF cuando el usuario elige CLP + una fecha.
  useEffect(() => {
    if (currency !== "CLP") { setUfRate(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/uf-rate?date=${priceDate}`);
        setUfRate(data.uf);
      } catch { setUfRate(null); }
    }, 250);
    return () => clearTimeout(t);
  }, [currency, priceDate]);

  const regenerate = async () => {
    if (!window.confirm("Regenerar contrato desde la plantilla? Esto sobrescribe cualquier edición manual.")) return;
    setSaving(true);
    try {
      const params = new URLSearchParams({ currency });
      if (currency === "CLP") params.append("price_date", priceDate);
      const { data } = await api.post(`/customers/${customerId}/contract/regenerate?${params.toString()}`);
      setContract(data);
      if (data.uf_rate_used) setUfRate(data.uf_rate_used);
      toast.success(currency === "CLP" && data.uf_rate_used
        ? `Contrato regenerado en CLP (UF ${new Date(priceDate).toLocaleDateString('es-CL')} = $${data.uf_rate_used.toLocaleString('es-CL')})`
        : "Contrato regenerado en UF");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const saveContract = async () => {
    setSaving(true);
    const { data } = await api.patch(`/customers/${customerId}/contract`, {
      html: contract.html,
      signing_city: contract.signing_city,
      signing_date: contract.signing_date,
      status: contract.status,
    });
    setContract({ ...contract, ...data });
    setSaving(false);
    setEditing(false);
    toast.success("Contrato actualizado");
  };

  const downloadHTML = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Contrato ${customerName}</title><style>${CONTRACT_STYLES}</style></head><body>${contract.html}</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Contrato_${customerName.replace(/\s+/g, "_")}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/customers/${customerId}/contract.pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Error generando PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Contrato_${customerName.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error generando PDF");
    } finally { setSaving(false); }
  };

  const uploadToDrive = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/contract/upload-drive`);
      toast.success("Contrato subido a Drive");
      if (data.webViewLink) window.open(data.webViewLink, "_blank");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo a Drive");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="contract-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-4xl border-t-4 border-[#FF5A00] max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Contrato</div>
            <div className="font-display font-bold text-xl">{customerName}</div>
          </div>
          <button onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>

        {contract ? (
          <>
            <div className="px-6 py-3 border-b border-neutral-200 bg-neutral-50 flex flex-wrap items-center gap-3">
              <Field label="Ciudad de firma">
                <input value={contract.signing_city || ""} onChange={(e) => setContract({ ...contract, signing_city: e.target.value })} className={`${inputCls} w-40`} />
              </Field>
              <Field label="Fecha de firma">
                <input type="date" value={contract.signing_date || ""} onChange={(e) => setContract({ ...contract, signing_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Precio en">
                <div className="inline-flex border border-neutral-300" data-testid="contract-currency-toggle">
                  <button type="button" onClick={() => setCurrency("UF")} data-testid="contract-currency-uf"
                    className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] ${currency === "UF" ? "bg-[#0A0A0A] text-white" : "bg-white text-neutral-700 hover:bg-neutral-50"}`}>UF</button>
                  <button type="button" onClick={() => setCurrency("CLP")} data-testid="contract-currency-clp"
                    className={`px-3 py-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] border-l border-neutral-300 ${currency === "CLP" ? "bg-[#0A0A0A] text-white" : "bg-white text-neutral-700 hover:bg-neutral-50"}`}>CLP</button>
                </div>
              </Field>
              {currency === "CLP" && (
                <Field label="Fecha valor UF" hint={ufRate ? `UF = $${ufRate.toLocaleString('es-CL')}` : "Cargando…"}>
                  <input type="date" value={priceDate} max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setPriceDate(e.target.value)}
                    className={inputCls} data-testid="contract-price-date" />
                </Field>
              )}
              <Field label="Estado">
                <select value={contract.status || "draft"} onChange={(e) => setContract({ ...contract, status: e.target.value })} className={inputCls} data-testid="contract-status-select">
                  <option value="draft">Borrador</option>
                  <option value="signed">Firmado</option>
                  <option value="terminated">Terminado</option>
                </select>
              </Field>
              <div className="ml-auto flex items-center gap-2">
                <SecondaryButton onClick={regenerate} disabled={saving} data-testid="contract-regenerate">
                  <RefreshCw className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Regenerar
                </SecondaryButton>
                <SecondaryButton onClick={downloadPDF} disabled={saving} data-testid="contract-download-pdf">
                  <Download className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> PDF
                </SecondaryButton>
                <SecondaryButton onClick={uploadToDrive} disabled={saving} data-testid="contract-upload-drive">
                  {contract.drive_file ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-[#00C853]" /> : <Cloud className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
                  {contract.drive_file ? "En Drive" : "Subir a Drive"}
                </SecondaryButton>
                <PrimaryButton onClick={() => setEditing((e) => !e)} data-testid="contract-edit-toggle">
                  {editing ? "Vista previa" : "Editar"}
                </PrimaryButton>
              </div>
            </div>

            <style>{CONTRACT_STYLES}</style>
            <div className="flex-1 overflow-auto bg-neutral-100 p-6" data-testid="contract-body">
              {editing ? (
                <div className="max-w-3xl mx-auto">
                  <RichTextEditor
                    testid="contract-rich-editor"
                    value={contract.html}
                    onChange={(html) => setContract({ ...contract, html })}
                  />
                </div>
              ) : (
                <div className="bg-white shadow-sm max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: sanitizeHtml(contract.html) }} />
              )}
            </div>

            <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 flex items-center justify-end gap-2">
              <SecondaryButton onClick={onClose}>Cerrar</SecondaryButton>
              <PrimaryButton onClick={saveContract} disabled={saving} data-testid="contract-save">
                <Save className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> {saving ? "Guardando…" : "Guardar contrato"}
              </PrimaryButton>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center py-16">
            <FileSignature className="w-6 h-6 text-neutral-400 animate-pulse" />
          </div>
        )}
      </div>
    </div>
  );
}
